import Big from 'big.js';
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import type { FavoriteCampaignInfoV1 } from '~/api/v1/mall/favorite-campaign';
import type { RewardTypeV1 } from '~/api/v1/schemas/reward-type';
import type { CampaignTransferResponseV2 } from '~/api/v2/mall/campaign/transfer/campaign-id';
import type { OfferDetailResponseV2 } from '~/api/v2/mall/offer/campaign-id';
import type { RecommendShopItemV2 } from '~/api/v2/mall/recommend-shop';
import type { SegmentResponseV2 } from '~/api/v2/mall/segment';
import type { ShopDetailResponseV2 } from '~/api/v2/mall/shop/campaign-id';
import type { CampaignInfoV2 } from '~/api/v2/schemas/campaign-info';
import { RewardTypeV2 } from '~/api/v2/schemas/reward-type';

import { extractDataFromProps } from './index.extract';
import type { PointPrimitiveStyles } from './index.patterns';
import { selectPattern } from './index.select';
import { type RewardTypeValue, selectRewardTypeValue } from './reward-type-value';

export type PointPrimitive2Styles = {
  box: string;
  prefix?: RewardTypeValue<string>;
  suffix?: RewardTypeValue<string>;
  num: RewardTypeValue<string>;
};

export type PointPrimitive2Props = {
  rewardType: RewardTypeV1 | RewardTypeV2;
  styles: PointPrimitive2Styles;
  numFormatter: RewardTypeValue<(num: Big | number) => ReactNode>;
  num: number;
  prefix?: RewardTypeValue<ReactNode>;
  suffix?: RewardTypeValue<ReactNode>;
};

/**
 * ポイント表示用のプリミティブコンポーネント
 *
 * `Point`コンポーネントの低レベル版で、より直接的なAPIを提供します。
 * データ抽出やパターン選択の処理を行わず、渡されたプロパティをそのまま使用してポイントを表示します。
 *
 * @example
 * ```tsx
 * // rate型（パーセント表示）の例
 * <PointPrimitive2
 *   rewardType={1}
 *   styles={{ box: '', num: 'text-lg font-bold', suffix: 'text-sm text-gray-600' }}
 *   numFormatter={{ rate: (n) => (n * 100).toFixed(1), amount: addComma }}
 *   num={0.015}
 *   suffix={{ rate: '%', amount: '円' }}
 * />
 *
 * // amount型（金額表示）の例
 * <PointPrimitive2
 *   rewardType={2}
 *   styles={{ box: '', num: 'text-lg', prefix: 'text-sm' }}
 *   numFormatter={addComma}
 *   num={1000}
 *   prefix="最大"
 *   suffix="円"
 * />
 * ```
 *
 * @remarks
 * - `Point`コンポーネントと異なり、APIレスポンスからのデータ抽出は行いません
 * - モール固有のカスタマイズや、シンプルなポイント表示に適しています
 * - prefix/suffixはRewardTypeに応じて異なる値を設定可能です
 */
export const PointPrimitive2 = (props: PointPrimitive2Props) => {
  const { rewardType, styles, numFormatter, num, prefix = '', suffix = '' } = props;

  // Performance optimization with useMemo for computed values
  const formatNum = useMemo(() => selectRewardTypeValue(numFormatter, rewardType), [numFormatter, rewardType]);

  const numContent = useMemo(() => formatNum(num), [formatNum, num]);
  const prefixContent = useMemo(() => selectRewardTypeValue(prefix, rewardType), [prefix, rewardType]);
  const suffixContent = useMemo(() => selectRewardTypeValue(suffix, rewardType), [suffix, rewardType]);

  // Style consistency: all style selections follow the same pattern
  const prefixClassName = useMemo(
    () => (styles.prefix ? selectRewardTypeValue(styles.prefix, rewardType) : undefined),
    [styles.prefix, rewardType]
  );
  const suffixClassName = useMemo(
    () => (styles.suffix ? selectRewardTypeValue(styles.suffix, rewardType) : undefined),
    [styles.suffix, rewardType]
  );
  const numClassName = useMemo(() => selectRewardTypeValue(styles.num, rewardType), [styles.num, rewardType]);

  return (
    <span className={styles.box}>
      {prefixContent ? <span className={prefixClassName}>{prefixContent}</span> : null}
      <span className={numClassName}>{numContent}</span>
      {suffixContent ? <span className={suffixClassName}>{suffixContent}</span> : null}
    </span>
  );
};

// PointStylesとPointParamの一部の値は、
// RewardType（rate/amount）に応じて別のStyle/文字列を指定することが可能。
// 共通で良い場合はstringで指定する。
// 分けたい場合は、{ rate: string; amount: string; }なobjectを指定する。

// Style指定（tailwindのクラス名のリストを記述する）
export type PointStyles = {
  box: string;
  stage: string;
  stageLabel: string;
  specialLabel: string;
  multiAdvertisementLabel: string;
  maxLabel: string;
  rangeLabel: string;
  pointupLabel: string;
  normal: PointPrimitiveStyles;
  regular: PointPrimitiveStyles;
  pointup: PointPrimitiveStyles;
  prefix: string;
  suffix: string;
};

export type AchievedMark = (achieved: boolean) => ReactNode;

// Point表記に補助的に使用する文字列を指定する
export type PointParam = {
  maxLabel: string;
  rangeLabel: string;
  pointupLabel: string;
  normal: {
    prefix: RewardTypeValue<string>;
    suffix: RewardTypeValue<string>;
  };
  regular: {
    prefix: RewardTypeValue<string>;
    suffix: RewardTypeValue<string>;
  };
  pointup: {
    prefix: RewardTypeValue<string>;
    suffix: RewardTypeValue<string>;
  };
  numFormatter: RewardTypeValue<(num: Big | number) => string>;
  prefix: ReactNode;
  suffix: ReactNode;
  mode: PointDisplayMode;
  achievedMark: AchievedMark;
  showMultiAdvertisementLabel: boolean;
};

export const PointDisplayMode = {
  MAX: 1,
  RANGE: 2,
  FULL: 3,
} as const;

export type PointDisplayMode = (typeof PointDisplayMode)[keyof typeof PointDisplayMode];

type PointPropsCommon = {
  styles: PointStyles;
  param: PointParam;
  segment?: SegmentResponseV2;
};

/// campaignInfo/recommendShopDetail/shopDetail/offerDetail/campaignTransfer/favoriteCampaignのどれかを受け取る
export type PointProps =
  | (PointPropsCommon & { campaignInfo: CampaignInfoV2 })
  | (PointPropsCommon & { recommendShopDetail: RecommendShopItemV2 })
  | (PointPropsCommon & { shopDetail: ShopDetailResponseV2 })
  | (PointPropsCommon & { offerDetail: OfferDetailResponseV2 })
  | (PointPropsCommon & { campaignTransfer: CampaignTransferResponseV2 })
  | (PointPropsCommon & { favoriteCampaign: FavoriteCampaignInfoV1 });
export const Point = (props: PointProps) => {
  const { campaignType, rewardType, net } = extractDataFromProps(props);

  const Pattern = selectPattern(props.segment, campaignType, net);

  const { param, styles } = props;

  return (
    <div className={styles.box}>
      <span className={styles.prefix}>{param.prefix}</span>
      <Pattern styles={styles} param={param} rewardType={rewardType} net={net} />
      <span className={styles.suffix}>{param.suffix}</span>
    </div>
  );
};
